import { EntityAdapter, createEntityAdapter } from "@ngrx/entity";
import { Action, createReducer, on } from "@ngrx/store";
import * as studentsActions from "./students.actions";
import { FileObject } from '@supabase/storage-js';
import { StudentDto } from "../interfaces/student_dto";

export const studentFeatureKey = "students";

export interface State {
    [studentFeatureKey]: StudentsState;
    isLoaded: boolean;
    hasLoadedAttachment: boolean;
}

export interface StudentsState extends EntityAdapter<StudentDto> {
    selectedStudent: number | null;
    isLoaded: boolean;
    hasLoadedAttachment: boolean;
}

export const adapter = createEntityAdapter<StudentDto>({
    selectId: (student: StudentDto) => String(student?.student_id) || "",
    sortComparer: false,
});

export const initialState = adapter.getInitialState({
    selectedStudent: null,
    isLoaded: false,
    hasLoadedAttachment: true,
});

const studentsReducer = createReducer(
    initialState,
    on(studentsActions.loadStudents,
        (state) => ({ ...state, isLoaded: false }),
    ),
    on(studentsActions.loadStudentsSuccess,
        (state, { students }) => adapter.setAll(students, { ...state, isLoaded: true }),
    ),
    on(studentsActions.selectStudent,
        (state, { student }) => ({ ...state, selectedStudent: student?.student_id }),
    ),
    on(studentsActions.addStudentSuccess,
        (state, { student }) => adapter.addOne(student, { ...state }),
    ),
    on(studentsActions.updateStudentSuccess, (state, { student }) => {
        const newState = adapter.updateOne({ id: student.student_id, changes: { ...student, attachments: student.attachments} }, { ...state });

        // If the updated student is the selected student, update selectedStudent as well
        if (state.selectedStudent && state.selectedStudent === student.student_id) {
            return { ...newState, selectedStudent: student.student_id };
        }

        return newState;
    }),
    on(studentsActions.deleteStudentSuccess, (state, { student }) => {
            const newState = adapter.removeOne(String(student.student_id), { ...state });

            if (state.selectedStudent === student.student_id) {
                return { ...newState, selectedStudent: null };
            }

            return newState;
        }
    ),
    on(studentsActions.loadStudentSuccess,
        (state, { student }) => adapter.updateOne({ id: student.student_id, changes: student }, { ...state })
    ),
    on(studentsActions.upsertStudents,
        (state, { students }) => adapter.upsertMany(students, { ...state }),
    ),
    on(studentsActions.deleteAttachmentSuccess,
        (state, { student, attachment }) => adapter.updateOne({ id: student.student_id, changes: { attachments: student.attachments.filter((a) => a !== attachment) } }, { ...state }),
    ),
    on(studentsActions.downloadAttachment,
        (state) => ({ ...state, hasLoadedAttachment: false }),
    ),
    on(studentsActions.openAttachmentSuccess,
        (state) => ({ ...state, hasLoadedAttachment: true }),
    ),
    on(studentsActions.openAttachmentFailed,
        (state) => ({ ...state, hasLoadedAttachment: true }),
    ),
);

export function studentReducer(
    state: any,
    action: Action,
) {
    return studentsReducer(state, action);
}
